import React from "react";
import { ThumbsUp, ThumbsDown, Hourglass } from "lucide-react";
import { CorpusStatus } from "@/constants/corpus.constants";

/**
 * Props for the CorpusStatItem component
 */
interface CorpusStatItemProps {
  /** Stat item title */
  title: string;
  /** Total count */
  total: number;
  /** Success count */
  success: number;
  /** Failed count */
  failed: number;
  /** In progress count */
  inProgress: number;
}

/**
 * CorpusStatItem component displays a stat card showing corpus title, total count,
 * and status breakdown (success, failed, in progress) with icons. Matches CHoP UI style.
 * 
 * @component
 * @param {CorpusStatItemProps} props - Component props
 * @param {string} props.title - Stat title
 * @param {number} props.total - Total count
 * @param {number} props.success - Success count
 * @param {number} props.failed - Failed count
 * @param {number} props.inProgress - In progress count
 * @returns {JSX.Element} Stat card with title, total, and status breakdown
 * 
 * @example
 * ```tsx
 * <CorpusStatItem
 *   title="Policy Documents"
 *   total={100}
 *   success={80}
 *   failed={10}
 *   inProgress={10}
 * />
 * ```
 */
export const CorpusStatItem: React.FC<CorpusStatItemProps> = ({
  title,
  total,
  success,
  failed,
  inProgress,
}) => {
  return (
    <div className="flex justify-between items-center w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      {/* Left Section */}
      <div className="flex flex-col gap-2 font-primary text-zinc-950">
        <span className="font-medium text-lg">{title}</span>
        <span className="text-3xl font-semibold">{total}</span>
      </div>

      {/* Right Section */}
      <div className="flex flex-col gap-2 text-sm font-medium">
        <div className="flex items-center gap-2 text-green-600">
          <ThumbsUp className="h-4 w-4" strokeWidth={2} />
          <span className="text-zinc-950">
            {success} <span>{CorpusStatus.SUCCESS}</span>
          </span>
        </div>

        <div className="flex items-center gap-2 text-red-600">
          <ThumbsDown className="h-4 w-4" strokeWidth={2} />
          <span className="text-zinc-950">
            {failed} <span>{CorpusStatus.FAILED}</span>
          </span>
        </div>

        <div className="flex items-center gap-2 text-orange-500">
          <Hourglass className="h-4 w-4" strokeWidth={2} />
          <span className="text-zinc-950">
            {inProgress} <span>{CorpusStatus.IN_PROGRESS}</span>
          </span>
        </div>
      </div>
    </div>
  );
};